import {
  collection,
  doc,
  DocumentReference,
  CollectionReference,
  updateDoc,
  Unsubscribe,
  onSnapshot,
  where,
  query,
  getDocs,
} from "firebase/firestore";
import { db } from "./firebaseCore";
import { Account, User, WithId, withRef, WithRef } from "@um/contracts";

// abstraction for contracts between the client and server
export {
  DocumentReference,
  DocumentSnapshot,
  Timestamp,
  FieldValue,
  arrayUnion,
  CollectionReference,
} from "firebase/firestore";

export interface DocumentData {
  [field: string]: any;
}

// Collection and Document Reference Helpers
export function getCollection<T = DocumentData>(
  path: string,
  parentRef?: DocumentReference
): CollectionReference<T> {
  if (parentRef) {
    return collection(parentRef, path) as CollectionReference<T>;
  }
  return collection(db, path) as CollectionReference<T>;
}

export function getDocument<T = DocumentData>(
  path: string,
  parentRef?: CollectionReference<T>
): DocumentReference<T> {
  if (parentRef) {
    return doc(parentRef, path) as DocumentReference<T>;
  }
  return doc(db, path) as DocumentReference<T>;
}

// Generate a new document ID
export const newId = () => {
  return doc(collection(db, "_")).id;
};

// Account Operations
export const getUserAccounts = async (
  userId: string | null
): Promise<WithRef<Account>[]> => {
  const accountsRef = getCollection<Account>("Account");

  const q = userId
    ? query(
        accountsRef,
        where("users", "array-contains", doc(db, "User", userId))
      )
    : query(accountsRef);

  const querySnapshot = await getDocs(q);

  return (
    querySnapshot.docs
      .map(withRef)
      // Legacy accounts can lack a creation date; keep them at the end of the list.
      .sort(
        (a, b) =>
          (b.created?.toDate().getTime() ?? 0) -
          (a.created?.toDate().getTime() ?? 0)
      )
  );
};

export const setDefaultAccount = async (userId: string, accountId: string) => {
  const userRef = doc(db, "User", userId);
  const accountRef = doc(db, "Account", accountId);

  await updateDoc(userRef, {
    defaultAccount: accountRef,
  });
};
